/*
 * shipping-options.js
 *
 * Przelicza tabelkę form dostawy przy zmianie wariantu produktu.
 * Cennik Cavalo jest wagowy, więc dla każdej metody wybieramy pierwszy
 * próg, który obsługuje wagę wybranego wariantu (progi w kolejności rosnącej).
 * Filtr po tagach robi Liquid — tagi nie zależą od wariantu.
 */
class ShippingOptions extends HTMLElement {
  constructor() {
    super();
    this.methods = this.parseJson(this.dataset.methods, []);
    this.variants = this.parseJson(this.dataset.variants, {});
    this.rows = this.querySelector('[data-shipping-options-rows]');
    this.currency = this.dataset.currency || 'zł';
    this.freeLabel = this.dataset.freeLabel || 'Darmowa';
    this.emptyText = this.dataset.emptyText || '';
    this.note = this.parentElement && this.parentElement.querySelector('.shipping-options__note');
  }

  connectedCallback() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event && event.data && event.data.variant;
      if (!variant || !variant.id) return;
      this.render(variant.id);
    });
  }

  disconnectedCallback() {
    if (this.unsubscriber) this.unsubscriber();
  }

  parseJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  render(variantId) {
    if (!this.rows || !this.methods.length) return;

    const variant = this.variants[String(variantId)];
    if (!variant) return;

    const tiers = this.tiersForVariant(variant);

    this.rows.innerHTML = tiers.length
      ? tiers.map((method) => this.rowHtml(method, variant)).join('')
      : `<tr><td class="shipping-options__empty" colspan="3">${this.emptyText}</td></tr>`;

    // Dopisek o wadze zamówienia nie ma sensu, gdy nie pokazujemy żadnej stawki.
    if (this.note) this.note.hidden = !tiers.length;
  }

  // Jedna pozycja na metodę: pierwszy próg, który obsłuży wariant.
  tiersForVariant(variant) {
    const kg = variant.kg;
    const chosen = [];
    const used = new Set();

    this.methods.forEach((method) => {
      if (used.has(method.name)) return;
      // Wariant bez ustawionej wagi (0 kg) trafia w pierwszy próg.
      if (method.maxKg && kg > method.maxKg) return;
      // Dolna granica progu — Shopify ma przedziały typu 20.01–40 kg,
      // bez tego wariant 20 kg złapałby stawkę z przedziału 20.01–40.
      if (method.minKg && kg < method.minKg) return;
      // Stawki warunkowane kwotą, nie wagą (darmowy InPost od 1500 zł).
      if (method.minPrice && variant.price < method.minPrice) return;
      used.add(method.name);
      chosen.push(method);
    });

    return chosen;
  }

  rowHtml(method, variant) {
    const isFree = !method.price || (method.freeFrom && variant.price >= method.freeFrom);
    const price = isFree
      ? `<strong class="shipping-options__free">${this.freeLabel}</strong>`
      : `${method.price.toFixed(2).replace('.', ',')} ${this.currency}${
          method.freeFrom
            ? `<span class="shipping-options__hint">(darmowa od ${Math.round(method.freeFrom)} ${this.currency})</span>`
            : ''
        }`;

    const logo = method.logo
      ? `<img src="${method.logo}" alt="${method.name}" width="28" height="28" onerror="this.closest('td').innerHTML=''">`
      : '';

    return `<tr><td class="shipping-options__logo">${logo}</td><td class="shipping-options__name">${method.name}</td><td class="shipping-options__price">${price}</td></tr>`;
  }
}

if (!customElements.get('shipping-options')) {
  customElements.define('shipping-options', ShippingOptions);
}
